import { useMutation } from "@tanstack/react-query";

import { useGetCustomerAccounts } from "~/features/payment/hooks/use-get-customer-accounts.ts";
import { usePostSubmitPayment } from "~/features/payment/hooks/use-post-payment.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import type {
	PaymentNonce,
	SavedAch,
	SavedCard,
} from "~/shared/types/payment-types.ts";

type SelectedPaymentMethod = SavedCard | SavedAch | PaymentNonce;

const calculateQuoteTotal = (quote: QuoteResponse): number => {
	const carrierPrices = (quote.carrierPrices ?? []) as CarrierPrice[];
	const carrierPrice = carrierPrices.find(
		(c) => c.id === quote.selectedCarrierPriceId,
	);
	return carrierPrice?.customerRate ?? 0;
};

const isAch = (paymentMethod: SelectedPaymentMethod): boolean => {
	if (paymentMethod.paymentMethod === "ACH") return true;
	return "accountType" in paymentMethod && Boolean(paymentMethod.accountType);
};

/**
 * Pre-paid customer tender payment via Braintree (V2 SubmitPayment).
 * Single-call replacement for the legacy JPM 3-step flow.
 *
 * Caller is expected to invoke this AFTER the initial tender call returns
 * `statusLevel === PaymentRequired`, and BEFORE the final tender call.
 */
export const usePrepaidPaymentForTender = ({
	enabled = true,
}: { enabled?: boolean } = {}) => {
	const { accounts } = useGetCustomerAccounts({ enabled });
	const { submitPaymentAsync } = usePostSubmitPayment();

	const { mutateAsync, isPending } = useMutation({
		mutationFn: async ({
			quote,
			paymentMethod,
			paymentNotes,
		}: {
			quote: QuoteResponse;
			paymentMethod: SelectedPaymentMethod;
			paymentNotes?: string;
		}) => {
			const totalAmount = calculateQuoteTotal(quote);
			const lastFour = paymentMethod.Last4 ?? "";
			const isAchPayment = isAch(paymentMethod);

			const payload = {
				paymentMerchantCustomerId: accounts?.paymentMerchantCustomerId ?? "",
				paymentMethodToken: paymentMethod.paymentMethodToken,
				appSourceType: "New Trax",
				// Backend contract: 1 = ACH, 2 = CreditCard.
				paymentMethod: isAchPayment ? 1 : 2,
				paymentAccountLastFourDigits: lastFour,
				totalPaymentAmount: totalAmount,
				paymentComments: paymentNotes ?? "",
				transactionDetails: [
					{
						amtPaid: totalAmount,
						invoiceID: String(quote.poNumber ?? ""),
						invoiceType: 2, // Prepaid
						poNumber: quote.poNumber ?? 0,
					},
				],
			};

			return submitPaymentAsync(payload);
		},
	});

	return {
		runPrepaidPayment: mutateAsync,
		isPaying: isPending,
		calculateQuoteTotal,
	};
};

export { calculateQuoteTotal };
